import LayoutContent, { FOOTER_NOTICE } from '../../components/layouts/content2';
import React, { useEffect, useRef, useState } from 'react';
import { getData, resolveImage } from '../../helpers/indexedDbHelper';
import {useTranslation} from 'react-i18next';
import translate from '../../helpers/translate';
import translateMenu from '../../helpers/translateMenu';
import { isCategoryVisible, useNow } from '../../helpers/schedule';
import { LIST_TABS, findCategory } from './menuConfig';
import { ScreenHeader } from './subcategories';

const PINK = '#E0157A';

// Video lateral de la lista. Kids Menu lleva el de postres/slushy en vez del de cocteles.
const SIDE_VIDEOS = {
    default: { src: require('./assets/list/banner-cocktails.mp4'), opens: 'SPECIALTY COCKTAILS' },
    'KIDS MENU': { src: require('./assets/list/banner-kids.mp4'), opens: 'DESSERTS' }
};

// Aviso que va debajo de las categorias en algunas secciones (precios/condiciones).
// `width` es el ancho del aviso segun el diseño; va centrado.
const CATEGORY_NOTICES = {
    'MAKE YOUR OWN COMBO': { src: require('./assets/list/notice-combo.png'), width: '100%' },
    'TACOS GRILLED': { src: require('./assets/list/notice-tacos.png'), width: '80%' },
    FAJITAS: { src: require('./assets/list/notice-fajitas.png'), width: '94%' },
    TEQUILA: { src: require('./assets/list/notice-tequila.png'), width: '100%' },
    'BURRITOS & QUESADILLAS': { src: require('./assets/list/notice-burritos.png'), width: '80%' }
};

const avigea = {
    fontFamily: "'Avigea', 'Times New Roman', serif",
    textTransform: 'uppercase',
    lineHeight: 1,
    margin: 0
};

const noScrollbar = `
.list-no-scrollbar { scrollbar-width: none; }
.list-no-scrollbar::-webkit-scrollbar { display: none; }
`;

// Nombre del grupo de LIST_TABS al que pertenece la categoria abierta
const groupOf = (category) => {
    const name = (category?.name || '').toUpperCase().trim();
    return LIST_TABS.flatMap((tab) => tab.categories)
        .find((group) => name === group || name.startsWith(`${group} >`));
};

const sans = "'Helvetica Neue', Arial, sans-serif";

// Foto horizontal del plato; si no hay, la vertical; si tampoco, el logo
function DishImage({ id, style }) {
    const [image, setImage] = useState(false);

    useEffect(() => {
        setImage(false);
        resolveImage(`menu-image-h-${id}`)
            .then((url) => url || resolveImage(`menu-image-${id}`))
            .then((url) => url && setImage(url));
    }, [id]);

    return (
        <div style={{ width: '100%', background: '#1a1a1a', ...style }}>
            {image
                ? <img src={image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                : <img src={require('./assets/home/logo.png')} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '12%', boxSizing: 'border-box', opacity: 0.85, display: 'block' }} />}
        </div>
    );
}

const dishName = (data, langEng, lang) => (langEng ? data.name : translate(lang, data.language, data.name));

const dishDescription = (data, langEng, lang) => (langEng || !data.language
    ? (data.regular_decription || '').replace(/\\n/g, '\n')
    : translateMenu(lang, data.language, data.regular_decription || '', 'regular_decription'));

const formatPrice = (price, langEng) => (price > 0
    ? `$${Number(price).toFixed(2)}`
    : (langEng ? 'Market price' : 'Precio de mercado'));

// Pago con tarjeta: 3.75% mas que en efectivo (ver FOOTER_NOTICE)
const CREDIT_SURCHARGE = 0.0375;
const creditPrice = (price) => Math.round(price * (1 + CREDIT_SURCHARGE) * 100) / 100;

// Precio en efectivo y con tarjeta, alineados a la derecha
function Prices({ price, langEng, size }) {
    if (!(price > 0)) return <span style={{ ...avigea, fontSize: size }}>{formatPrice(price, langEng)}</span>;

    return (
        <div style={{ ...avigea, flexShrink: 0, textAlign: 'right', lineHeight: 1.1 }}>
            <div style={{ fontSize: size }}>{langEng ? 'CASH' : 'EFECTIVO'}: {formatPrice(price, langEng)}</div>
            <div style={{ fontSize: `calc(${size} * 0.75)` }}>{langEng ? 'CREDIT' : 'TARJETA'}: {formatPrice(creditPrice(price), langEng)}</div>
        </div>
    );
}

function Item({ data, langEng, lang, onSelect }) {
    const description = dishDescription(data, langEng, lang);

    return (
        <div
            onClick={onSelect}
            style={{
                flexShrink: 0,
                background: '#000',
                borderRadius: 22,
                overflow: 'hidden',
                cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent'
            }}
        >
            <DishImage id={data.id} style={{ aspectRatio: '492 / 340' }} />
            <div style={{ padding: '1.8vh 6% 2.2vh', textAlign: 'left' }}>
                <h3 style={{ ...avigea, color: '#fff', fontSize: 'clamp(22px, 3vw, 40px)' }}>
                    {dishName(data, langEng, lang)}
                </h3>
                {description && (
                    <p
                        style={{
                            margin: '0.8vh 0 0',
                            color: '#fff',
                            fontFamily: sans,
                            fontSize: 'clamp(14px, 1.8vw, 22px)',
                            lineHeight: 1.3,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                        }}
                    >
                        {description}
                    </p>
                )}
            </div>
        </div>
    );
}

// Detalle de un plato: foto grande, nombre, precio, descripcion y sabores.
// Deslizar a los lados pasa al plato anterior/siguiente de la categoria.
function DishDetail({ dish, dishes, langEng, lang, onChange, onDrinks, onDesserts }) {
    const touchStart = useRef(null);
    const flavors = dish.flavors || [];

    const onTouchEnd = (e) => {
        if (touchStart.current === null || dishes.length < 2) return;
        const delta = e.changedTouches[0].clientX - touchStart.current;
        touchStart.current = null;
        if (Math.abs(delta) < 60) return;
        const index = dishes.findIndex((item) => item.id === dish.id);
        onChange(dishes[(index + (delta < 0 ? 1 : dishes.length - 1)) % dishes.length]);
    };

    // En pantallas bajas los botones se achican para dejarle espacio al plato
    const sectionButton = { width: 'auto', maxWidth: '100%', height: 'auto', maxHeight: '19vh', justifySelf: 'center', display: 'block', cursor: 'pointer', WebkitTapHighlightColor: 'transparent' };

    return (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: '2.5vh', marginTop: '3vh' }}>
            <div
                className='list-no-scrollbar'
                onTouchStart={(e) => { touchStart.current = e.touches[0].clientX; }}
                onTouchEnd={onTouchEnd}
                style={{ flex: '0 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#000', borderRadius: 22 }}
            >
                {/* Si no cabe todo, se recorta primero la foto y el texto queda siempre completo */}
                <DishImage id={dish.id} style={{ aspectRatio: '1070 / 485', flex: '0 1 auto', minHeight: '14vh' }} />

                <div className='list-no-scrollbar' style={{ flex: '0 0.001 auto', minHeight: 0, overflowY: 'auto', padding: '2.5vh 5% 3vh', textAlign: 'left', color: '#fff' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '4%' }}>
                        <h2 style={{ ...avigea, fontSize: 'clamp(30px, 4.6vw, 60px)' }}>
                            {dishName(dish, langEng, lang)}
                        </h2>
                        {flavors.length === 0 && (
                            <Prices price={dish.price} langEng={langEng} size="clamp(22px, 3.3vw, 44px)" />
                        )}
                    </div>

                    <p style={{ margin: '1.2vh 0 0', fontFamily: sans, fontSize: 'clamp(16px, 2.2vw, 28px)', lineHeight: 1.3, whiteSpace: 'pre-line' }}>
                        {dishDescription(dish, langEng, lang)}
                    </p>

                    {flavors.length > 0 && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.2vh 4%', marginTop: '2.5vh' }}>
                            {flavors.map((flavor, key) => (
                                <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '1.2vh 0', borderBottom: '1px solid #333', fontFamily: sans, fontSize: 'clamp(15px, 2vw, 24px)' }}>
                                    <span>{flavor.name}</span>
                                    <Prices price={flavor.price} langEng={langEng} size="clamp(15px, 2vw, 24px)" />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4.5%', flexShrink: 0 }}>
                <img src={require('./assets/list/btn-drinks.png')} alt="Drinks" style={sectionButton} onClick={onDrinks} />
                <img src={require('./assets/list/btn-desserts.png')} alt="Desserts" style={sectionButton} onClick={onDesserts} />
            </div>
        </div>
    );
}

function Index({dishs, setShowListProduct, goHome}) {

    const {i18n} = useTranslation();
    const langEng = i18n.language === 'en' ?? false;
    const now = useNow();

    const [menusData, setMenusData] = useState([]);
    const [current, setCurrent] = useState(dishs || {});
    const [selected, setSelected] = useState(false);
    const listRef = useRef(null);
    const pillsRef = useRef(null);

    useEffect(() => {
        getData('menu').then((items) => {
            // Sin menu en disco todavia: no reventar la app entera.
            if (!items?.value?.data) return;
            setMenusData(items.value.data);
        });
    }, []);

    // Si se acaba el horario de la categoria abierta (ej. Lunch), volver atras
    useEffect(() => {
        if (current?.name && !isCategoryVisible(current, now)) {
            setShowListProduct(false);
        }
    }, [now]);

    const activeGroup = groupOf(current);
    const activeTab = LIST_TABS.find((tab) => tab.categories.includes(activeGroup)) || LIST_TABS[0];
    const pills = activeTab.categories
        .map((name) => ({ name, category: findCategory(menusData, name, now) }))
        .filter((pill) => pill.category);

    // Al cambiar de categoria: lista arriba y pastilla activa a la vista
    useEffect(() => {
        if (listRef.current) listRef.current.scrollTop = 0;
        const active = pillsRef.current?.querySelector('[data-active="true"]');
        if (active) active.scrollIntoView({ inline: 'center', block: 'nearest' });
    }, [current, menusData]);

    const openGroup = (name) => {
        const category = findCategory(menusData, name, now);
        if (!category) return;
        setSelected(false);
        setCurrent(category);
    };

    const openTab = (tab) => {
        const first = tab.categories.find((name) => findCategory(menusData, name, now));
        if (first && (tab.key !== activeTab.key || selected)) openGroup(first);
    };

    const pillLabel = ({ name, category }) => {
        if (langEng || category.name !== name) return name;
        return translateMenu(i18n.language, category.language || [], name, 'name');
    };

    const menus = current?.menus || [];
    const sideVideo = SIDE_VIDEOS[activeGroup] || SIDE_VIDEOS.default;

    return (
        <div style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 9999}}>
            <LayoutContent showBack={false} showLogo={false} showLang={false} showFooter={false} goHome={goHome} setShowListProduct={setShowListProduct}>
                <style>{noScrollbar}</style>

                <div
                    onContextMenu={(e) => e.preventDefault()}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        boxSizing: 'border-box',
                        padding: '2.5vh 7% 1.5vh',
                        backgroundColor: '#f2f2f2',
                        backgroundImage: `url(${require('./assets/home/background.png')})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                    }}
                >
                    <ScreenHeader langEng={langEng} onBack={() => (selected ? setSelected(false) : setShowListProduct(false))} />

                    {/* Pestañas Food Menu / Drinks */}
                    <div style={{ display: 'flex', marginTop: '2.5vh', background: '#000', borderRadius: 24, flexShrink: 0 }}>
                        {LIST_TABS.map((tab) => {
                            const active = tab.key === activeTab.key;
                            return (
                                <div
                                    key={tab.key}
                                    onClick={() => openTab(tab)}
                                    style={{
                                        ...avigea,
                                        flex: 1,
                                        textAlign: 'center',
                                        padding: '2vh 0',
                                        borderRadius: 24,
                                        border: `3px solid ${active ? PINK : 'transparent'}`,
                                        background: active ? '#f7f5f2' : 'transparent',
                                        color: active ? '#000' : '#fff',
                                        fontSize: 'clamp(28px, 4vw, 54px)',
                                        cursor: 'pointer',
                                        WebkitTapHighlightColor: 'transparent'
                                    }}
                                >
                                    {langEng || tab.key === 'food' ? tab.label : 'BEBIDAS'}
                                </div>
                            );
                        })}
                    </div>

                    {/* Categorias de la pestaña activa */}
                    <div
                        ref={pillsRef}
                        className='list-no-scrollbar'
                        style={{ display: 'flex', gap: '1.6vw', overflowX: 'auto', marginTop: '1.4vh', flexShrink: 0 }}
                    >
                        {pills.map((pill) => {
                            const active = pill.name === activeGroup;
                            return (
                                <div
                                    key={pill.name}
                                    data-active={active}
                                    onClick={() => openGroup(pill.name)}
                                    style={{
                                        ...avigea,
                                        flexShrink: 0,
                                        whiteSpace: 'nowrap',
                                        padding: '1.2vh 2.2vw',
                                        borderRadius: 16,
                                        border: `2px solid ${active ? PINK : '#000'}`,
                                        background: active ? '#fff' : '#000',
                                        color: active ? '#000' : '#fff',
                                        fontSize: 'clamp(16px, 2.3vw, 30px)',
                                        cursor: 'pointer',
                                        WebkitTapHighlightColor: 'transparent'
                                    }}
                                >
                                    {pillLabel(pill)}
                                </div>
                            );
                        })}
                    </div>

                    {CATEGORY_NOTICES[activeGroup] && (
                        <img
                            src={CATEGORY_NOTICES[activeGroup].src}
                            alt=""
                            style={{ width: CATEGORY_NOTICES[activeGroup].width, height: 'auto', alignSelf: 'center', marginTop: '1.8vh', flexShrink: 0, pointerEvents: 'none' }}
                        />
                    )}

                    {selected && (
                        <DishDetail
                            dish={selected}
                            dishes={menus}
                            langEng={langEng}
                            lang={i18n.language}
                            onChange={setSelected}
                            onDrinks={() => openTab(LIST_TABS.find((tab) => tab.key === 'drinks'))}
                            onDesserts={() => openGroup('DESSERTS')}
                        />
                    )}

                    {/* Video + productos */}
                    <div style={{ flex: 1, minHeight: 0, display: selected ? 'none' : 'flex', gap: '1.6vw', marginTop: '3vh' }}>
                        <video
                            autoPlay
                            loop
                            muted
                            playsInline
                            disablePictureInPicture
                            key={sideVideo.src}
                            src={sideVideo.src}
                            onClick={() => openGroup(sideVideo.opens)}
                            style={{
                                width: '47%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: 36,
                                background: '#000',
                                cursor: 'pointer'
                            }}
                        />

                        <div
                            ref={listRef}
                            className='list-no-scrollbar'
                            style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '2vh' }}
                        >
                            {menus.map((item) => (
                                <Item
                                    key={item.id}
                                    data={item}
                                    langEng={langEng}
                                    lang={i18n.language}
                                    onSelect={() => setSelected(item)}
                                />
                            ))}
                        </div>
                    </div>

                    <p
                        style={{
                            margin: '1.5vh 0 0',
                            flexShrink: 0,
                            textAlign: 'center',
                            fontFamily: 'Noodle',
                            fontSize: 'clamp(12px, 1.3vw, 17px)',
                            lineHeight: 1.15,
                            letterSpacing: 0.5,
                            color: '#000',
                            textTransform: 'uppercase'
                        }}
                    >
                        {langEng ? FOOTER_NOTICE.en : FOOTER_NOTICE.es}
                    </p>
                </div>

            </LayoutContent>
        </div>
    );
}
export default Index;
